/**
 * Čtení čárových kódů EAN-13, EAN-8 a UPC-A z obrázku — bez knihovny.
 *
 * Proč vlastní dekodér: `BarcodeDetector` má Android Chrome, ale iOS Safari ne,
 * a technici mají iPhony. Přidat kvůli tomu balík (zxing ~300 kB wasm) by
 * znamenalo závislost navíc; EAN z krabice je přitom pár desítek řádků kódu.
 *
 * Postup je stejný jako u každé čtečky: vezmi vodorovný řádek pixelů, převeď
 * na černá/bílá, změř šířky pruhů a mezer a porovnej je se vzorem číslic.
 * Kódy mají kontrolní číslici, takže špatné čtení se pozná a zahodí.
 */

// Šířky čtyř prvků jedné číslice v modulech (L-kód; R-kód má stejné šířky,
// jen začíná pruhem, G-kód je L-kód pozpátku). Ověřeno proti normě EAN-13.
const CISLICE = [
  [3, 2, 1, 1], [2, 2, 2, 1], [2, 1, 2, 2], [1, 4, 1, 1], [1, 1, 3, 2],
  [1, 2, 3, 1], [1, 1, 1, 4], [1, 3, 1, 2], [1, 2, 1, 3], [3, 1, 1, 2],
];
const CISLICE_G = CISLICE.map((v) => [...v].reverse());

// Podle toho, které z prvních šesti číslic jsou v G-kódu, se pozná první číslice.
const PARITA = ["LLLLLL", "LLGLGG", "LLGGLG", "LLGGGL", "LGLLGG",
  "LGGLLG", "LGGGLL", "LGLGLG", "LGLGGL", "LGGLGL"];

const MODULU_CISLICE = 7;
const TOLERANCE = 0.75;      // povolená odchylka šířky prvku v modulech

/** Jak moc se naměřené šířky liší od vzoru. Vrací nekonečno, když nesedí vůbec. */
function odchylka(namerene, vzor) {
  const soucet = namerene.reduce((a, b) => a + b, 0);
  const modulu = vzor.reduce((a, b) => a + b, 0);
  if (soucet < modulu) return Infinity;            // užší než minimum → šum
  const jednotka = soucet / modulu;
  const strop = jednotka * TOLERANCE;
  let celkem = 0;
  for (let i = 0; i < namerene.length; i++) {
    const rozdil = Math.abs(namerene[i] - vzor[i] * jednotka);
    if (rozdil > strop) return Infinity;
    celkem += rozdil;
  }
  return celkem / jednotka;
}

function najdiCislici(sirky, tabulka) {
  let nej = { cislice: -1, odchylka: Infinity };
  for (let c = 0; c < 10; c++) {
    const o = odchylka(sirky, tabulka[c]);
    if (o < nej.odchylka) nej = { cislice: c, odchylka: o };
  }
  return nej.cislice >= 0 && nej.odchylka < Infinity ? nej : null;
}

/** Kontrolní číslice EAN/UPC: váhy 3 a 1 střídavě zprava. */
export function kontrolniSedi(kod) {
  if (!/^\d{8}$|^\d{12,13}$/.test(kod)) return false;
  const cislice = kod.split("").map(Number);
  const kontrolni = cislice.pop();
  let soucet = 0;
  for (let i = cislice.length - 1, vaha = 3; i >= 0; i--, vaha = vaha === 3 ? 1 : 3) {
    soucet += cislice[i] * vaha;
  }
  return (10 - (soucet % 10)) % 10 === kontrolni;
}

const OKNO = 40;   // šířka okna pro klouzavý průměr (px)

/**
 * Práh počítaný z klouzavého průměru okolí, ne z celého řádku.
 *
 * Jeden práh na celý řádek selže, jakmile na krabici leží stín nebo odlesk:
 * půlka kódu je pak „černá" a půlka „bílá". Průměr se posouvá plynule, takže
 * nevznikají skoky na hranicích oken; v jednolité ploše je pixel roven průměru
 * a tím pádem světlý — z čistého papíru se tak pruhy nevyrobí.
 */
function prahy(radek) {
  const n = radek.length;
  const soucty = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) soucty[i + 1] = soucty[i] + radek[i];
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const od = Math.max(0, i - OKNO);
    const do_ = Math.min(n, i + OKNO + 1);
    out[i] = (soucty[do_] - soucty[od]) / (do_ - od) - 4;
  }
  return out;
}

/** Šířky souvislých úseků stejné barvy. */
function useky(radek, prah) {
  const out = [];
  let barva = radek[0] <= prah[0] ? 1 : 0;   // 1 = tmavá
  let delka = 0;
  for (let i = 0; i < radek.length; i++) {
    const b = radek[i] <= prah[i] ? 1 : 0;
    if (b === barva) { delka++; } else { out.push({ barva, delka }); barva = b; delka = 1; }
  }
  out.push({ barva, delka });
  return out;
}

function sirky(u, od, kolik) {
  const out = [];
  for (let i = 0; i < kolik; i++) out.push(u[od + i].delka);
  return out;
}

/** Rozluští kód začínající úvodní značkou na pozici `zacatek`. */
function zkusOdPozice(u, zacatek) {
  // úvodní značka 101 — tři stejně široké prvky
  const uvod = sirky(u, zacatek, 3);
  const modul = (uvod[0] + uvod[1] + uvod[2]) / 3;
  if (modul < 1) return null;
  if (uvod.some((s) => Math.abs(s - modul) > modul * TOLERANCE)) return null;

  const zkusDelku = (pocetPulka, sEanParitou) => {
    const potreba = 3 + pocetPulka * 4 + 5 + pocetPulka * 4 + 3;
    if (zacatek + potreba > u.length) return null;

    let i = zacatek + 3;
    const leva = [];
    let parita = "";
    for (let d = 0; d < pocetPulka; d++, i += 4) {
      const s = sirky(u, i, 4);
      const jakoL = najdiCislici(s, CISLICE);
      const jakoG = sEanParitou ? najdiCislici(s, CISLICE_G) : null;
      if (!jakoL && !jakoG) return null;
      const lepsiG = jakoG && (!jakoL || jakoG.odchylka < jakoL.odchylka);
      leva.push(lepsiG ? jakoG.cislice : jakoL.cislice);
      parita += lepsiG ? "G" : "L";
    }

    // prostřední značka 01010 — pět prvků po jednom modulu
    const stred = sirky(u, i, 5);
    if (stred.some((s) => Math.abs(s - modul) > modul * TOLERANCE)) return null;
    i += 5;

    const prava = [];
    for (let d = 0; d < pocetPulka; d++, i += 4) {
      const nalez = najdiCislici(sirky(u, i, 4), CISLICE);
      if (!nalez) return null;
      prava.push(nalez.cislice);
    }

    const konec = sirky(u, i, 3);
    if (konec.some((s) => Math.abs(s - modul) > modul * TOLERANCE)) return null;

    if (!sEanParitou) {                       // EAN-8: osm číslic, žádná parita
      const kod = [...leva, ...prava].join("");
      return kontrolniSedi(kod) ? kod : null;
    }
    const prvni = PARITA.indexOf(parita);
    if (prvni < 0) return null;
    const kod = `${prvni}${leva.join("")}${prava.join("")}`;
    return kontrolniSedi(kod) ? kod : null;
  };

  return zkusDelku(6, true) || zkusDelku(4, false);
}

/** Zkusí přečíst kód z jednoho řádku jasů (0–255). */
export function dekodujRadek(radek) {
  let min = 255;
  let max = 0;
  for (let i = 0; i < radek.length; i++) {
    if (radek[i] < min) min = radek[i];
    if (radek[i] > max) max = radek[i];
  }
  // Málo kontrastní řádek nemá smysl luštit — je to stín nebo prázdno.
  if (max - min < 45) return null;

  const u = useky(radek, prahy(radek));
  for (const smer of [u, [...u].reverse()]) {   // kód může být i vzhůru nohama
    // úvodní značka začíná tmavým prvkem, proto jen ty
    for (let i = 0; i < smer.length - 20; i++) {
      if (smer[i].barva !== 1) continue;
      const kod = zkusOdPozice(smer, i);
      if (kod) return kod;
    }
  }
  return null;
}

/**
 * Projde snímek po vodorovných řádcích a vrátí kód, který se přečetl dvakrát.
 * Dvojí shoda + kontrolní číslice = prakticky nulová šance na překlep.
 */
export function dekodujSnimek(data, sirka, vyska, radku = 24) {
  const nalezy = new Map();
  const krok = Math.max(1, Math.floor(vyska / (radku + 1)));
  const radek = new Uint8Array(sirka);

  for (let y = krok; y < vyska; y += krok) {
    const zaklad = y * sirka * 4;
    for (let x = 0; x < sirka; x++) {
      const p = zaklad + x * 4;
      // jas podle citlivosti oka; stačí celočíselně, jde jen o práh
      radek[x] = (data[p] * 77 + data[p + 1] * 151 + data[p + 2] * 28) >> 8;
    }
    const kod = dekodujRadek(radek);
    if (!kod) continue;
    const kolikrat = (nalezy.get(kod) || 0) + 1;
    if (kolikrat >= 2) return kod;
    nalezy.set(kod, kolikrat);
  }
  return null;
}
